import { MEDALS, Medal } from "../data/eventModes";
import { bossTierOpen, medalsOwed } from "./eventModes";

/**
 * What the week's event mode has paid and where it stands (MP-21, docs/MODES.md §5; save section `eventModes`): the
 * player's power anchor locked at the week's first look, Boss Week's cleared tiers, Race Week's best time and medals
 * paid, the Odyssey's attempts, current map and life pool. Everything is keyed by the week (its Monday's day number):
 * a new week starts fresh, and a run of an older week counts nothing. Each prize pays once, and a run counts once
 * (by its key), however often its end screen is drawn.
 */

/** Run keys remembered so each counts once. */
const KEYS_KEPT = 16;

export interface EventModesStateJson {
  week: number;
  anchor: number;
  boss: number[];
  race: { best: number; paid: string[]; runs: number };
  odyssey: { used: number; live: boolean; map: number; lives: number; begun: boolean; maps: number[]; full: boolean };
  keys: string[];
}

/** What a Boss Week run did: the tier newly cleared (its prize pays) or nothing. */
export interface BossRecord { readonly counted: boolean; readonly cleared: boolean }
/** What a Race Week run did: its medal, a new best, the medals it pays. */
export interface RaceRecord { readonly counted: boolean; readonly newBest: boolean; readonly best: number; readonly pay: readonly Medal[] }
/** What an Odyssey map did: its first clear this week (its prize pays), the whole Odyssey's (once), the attempt over. */
export interface OdysseyRecord { readonly counted: boolean; readonly mapPaid: boolean; readonly fullPaid: boolean; readonly over: boolean; readonly lives: number }

/** The Odyssey run to play next: its map (1-based), the life pool it starts with (0 = full) and whether it opens an attempt. */
export interface OdysseyNext { readonly map: number; readonly lives: number; readonly newAttempt: boolean }

const NOT_COUNTED_RACE: RaceRecord = { counted: false, newBest: false, best: 0, pay: [] };
const NOT_COUNTED_ODYSSEY: OdysseyRecord = { counted: false, mapPaid: false, fullPaid: false, over: false, lives: 0 };

const isNat = (v: unknown): v is number => Number.isInteger(v) && (v as number) >= 0;
const natList = (v: unknown): number[] => (Array.isArray(v) ? [...new Set(v.filter((x): x is number => isNat(x) && x >= 1))].sort((a, b) => a - b) : []);

export class EventModesState {
  week = -1;
  anchor = 1;
  private bossTiers: number[] = [];
  raceBest = 0;
  private racePaid: Medal[] = [];
  raceRuns = 0;
  odysseyUsed = 0;
  odysseyLive = false;
  odysseyMapAt = 1;
  odysseyLives = 0;
  private odysseyBegun = false;
  private odysseyMaps: number[] = [];
  odysseyFull = false;
  private keys: string[] = [];

  /** Rolls to the week starting on `monday` the first time it is seen (fresh progress, the anchor locked); an older week is ignored. */
  enter(monday: number, highestCleared: number): void {
    if (monday <= this.week) return;
    const fresh = new EventModesState();
    Object.assign(this, fresh, { keys: this.keys });
    this.week = monday;
    this.anchor = Math.max(1, highestCleared);
  }

  private remember(key: string): boolean {
    if (this.keys.includes(key)) return false;
    this.keys.push(key);
    if (this.keys.length > KEYS_KEPT) this.keys.splice(0, this.keys.length - KEYS_KEPT);
    return true;
  }

  /** Whether a run of week `monday` with `key` counts now (its week is this one, and it has not counted). */
  private counts(monday: number, key: string): boolean { return monday === this.week && this.week >= 0 && this.remember(key); }

  // ---------------------------------------------------------------- Boss Week
  get bossCleared(): readonly number[] { return this.bossTiers; }
  bossOpen(tier: number): boolean { return bossTierOpen(this.bossTiers, tier); }

  /** A Boss Week run ended: a win of an open tier not yet cleared clears it (its prize pays once). */
  recordBoss(monday: number, tier: number, won: boolean, key: string): BossRecord {
    if (!this.counts(monday, key)) return { counted: false, cleared: false };
    if (!won || !this.bossOpen(tier) || this.bossTiers.includes(tier)) return { counted: true, cleared: false };
    this.bossTiers = [...this.bossTiers, tier].sort((a, b) => a - b);
    return { counted: true, cleared: true };
  }

  // ---------------------------------------------------------------- Race Week
  get medalsPaid(): readonly Medal[] { return this.racePaid; }

  /** A Race Week run ended: a clear keeps the week's best time and pays the medals its time earns that are still owed. */
  recordRace(monday: number, won: boolean, ms: number, medal: Medal | "", key: string): RaceRecord {
    if (!this.counts(monday, key)) return NOT_COUNTED_RACE;
    this.raceRuns++;
    if (!won) return { counted: true, newBest: false, best: this.raceBest, pay: [] };
    const newBest = this.raceBest === 0 || ms < this.raceBest;
    if (newBest) this.raceBest = ms;
    const pay = medalsOwed(medal, this.racePaid);
    this.racePaid = MEDALS.filter((m) => this.racePaid.includes(m) || pay.includes(m));
    return { counted: true, newBest, best: this.raceBest, pay };
  }

  // ---------------------------------------------------------------- Odyssey
  get odysseyCleared(): readonly number[] { return this.odysseyMaps; }

  /** The run to play next (the running attempt's map, else a new attempt's first), or null when none is left. */
  odysseyNext(attempts: number): OdysseyNext | null {
    if (this.odysseyLive) return { map: this.odysseyMapAt, lives: this.odysseyLives, newAttempt: false };
    return this.odysseyUsed < attempts ? { map: 1, lives: 0, newAttempt: true } : null;
  }

  /** The running attempt's map was started and has not finished (it may be suspended, to continue). */
  get odysseyInProgress(): boolean { return this.odysseyLive && this.odysseyBegun; }

  /** The running map was abandoned (no suspended copy is left of it): the attempt is over. */
  abandonOdyssey(): void {
    if (!this.odysseyInProgress) return;
    this.odysseyLive = false; this.odysseyBegun = false; this.odysseyLives = 0;
  }

  /** Attempts left this week (a running one not counted). */
  attemptsLeft(attempts: number): number { return Math.max(0, attempts - this.odysseyUsed); }

  /**
   * A fresh map run started (the level flow: not a resumed one). A map begun again before it finished was abandoned:
   * that attempt is over. Map 1 with no attempt running opens one (an attempt spent).
   */
  beginOdyssey(monday: number, map: number, attempts: number): void {
    if (monday !== this.week) return;
    if (this.odysseyLive && this.odysseyBegun) this.odysseyLive = false;
    if (!this.odysseyLive) {
      if (map !== 1 || this.odysseyUsed >= attempts) return;
      this.odysseyUsed++;
      this.odysseyLive = true; this.odysseyMapAt = 1; this.odysseyLives = 0;
    }
    if (map === this.odysseyMapAt) this.odysseyBegun = true;
  }

  /**
   * An Odyssey map ended. A win of the attempt's current map carries the Fortress HP left to the next map and pays the
   * map's prize the first time this week; the last map also pays the full Odyssey's (once). A loss ends the attempt.
   */
  recordOdyssey(monday: number, map: number, won: boolean, livesLeft: number, maps: number, key: string): OdysseyRecord {
    if (!this.counts(monday, key) || !this.odysseyLive || map !== this.odysseyMapAt) return NOT_COUNTED_ODYSSEY;
    this.odysseyBegun = false;
    if (!won || livesLeft <= 0) { this.odysseyLive = false; this.odysseyLives = 0; return { counted: true, mapPaid: false, fullPaid: false, over: true, lives: 0 }; }
    const mapPaid = !this.odysseyMaps.includes(map);
    if (mapPaid) this.odysseyMaps = [...this.odysseyMaps, map].sort((a, b) => a - b);
    this.odysseyLives = livesLeft;
    if (map < maps) { this.odysseyMapAt = map + 1; return { counted: true, mapPaid, fullPaid: false, over: false, lives: livesLeft }; }
    this.odysseyLive = false;
    const fullPaid = !this.odysseyFull;
    this.odysseyFull = true;
    return { counted: true, mapPaid, fullPaid, over: true, lives: livesLeft };
  }

  toJson(): EventModesStateJson {
    return {
      week: this.week, anchor: this.anchor, boss: [...this.bossTiers],
      race: { best: this.raceBest, paid: [...this.racePaid], runs: this.raceRuns },
      odyssey: { used: this.odysseyUsed, live: this.odysseyLive, map: this.odysseyMapAt, lives: this.odysseyLives, begun: this.odysseyBegun, maps: [...this.odysseyMaps], full: this.odysseyFull },
      keys: [...this.keys],
    };
  }

  /** Restores field by field: a malformed field keeps its fresh value. */
  static fromJson(j: unknown): EventModesState {
    const s = new EventModesState();
    if (typeof j !== "object" || j === null) return s;
    const src = j as Partial<EventModesStateJson>;
    if (Number.isInteger(src.week)) s.week = src.week!;
    if (isNat(src.anchor) && src.anchor >= 1) s.anchor = src.anchor;
    s.bossTiers = natList(src.boss);
    const r = src.race;
    if (r && typeof r === "object") {
      if (isNat(r.best)) s.raceBest = r.best;
      if (isNat(r.runs)) s.raceRuns = r.runs;
      if (Array.isArray(r.paid)) s.racePaid = MEDALS.filter((m) => r.paid.includes(m));
    }
    const o = src.odyssey;
    if (o && typeof o === "object") {
      if (isNat(o.used)) s.odysseyUsed = o.used;
      s.odysseyLive = o.live === true;
      if (isNat(o.map) && o.map >= 1) s.odysseyMapAt = o.map;
      if (isNat(o.lives)) s.odysseyLives = o.lives;
      s.odysseyBegun = o.begun === true;
      s.odysseyMaps = natList(o.maps);
      s.odysseyFull = o.full === true;
    }
    if (Array.isArray(src.keys)) s.keys = src.keys.filter((k): k is string => typeof k === "string").slice(-KEYS_KEPT);
    return s;
  }
}
